import React from "react";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { Link } from "react-router-dom";
import { Globe2, Users, Map as MapIcon, Landmark, CheckCircle2, AlertTriangle, ArrowRight } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { fmtNumber } from "@/lib/i18n";
import { API } from "@/lib/api";
import { OVERVIEW } from "@/lib/overviewContent";
import IntroSection from "@/components/IntroSection";

export const fetchOverview = async () => (await axios.get(`${API}/overview`)).data;
const BLOC_KEYS = ["arab", "eu", "us"];

export const fmtBig = (n, lang, unit) => {
  if (unit === "T") return `${fmtNumber(Math.round(n / 1e10) / 100, lang)} T$`;
  if (unit === "M") return `${fmtNumber(Math.round(n / 1e5) / 10, lang)} M`;
  if (unit === "Mkm2") return `${fmtNumber(Math.round(n / 1e4) / 100, lang)} M km²`;
  return fmtNumber(n, lang);
};

export function StatCard({ icon: Icon, label, value, sub, testid }) {
  return (
    <div className="rounded-2xl border border-stoneLine bg-card p-4 sm:p-5" data-testid={testid}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.15em] text-muted-foreground"><Icon className="w-3.5 h-3.5 text-ochre" /> {label}</div>
      <div className="stat-number text-2xl sm:text-3xl text-ink mt-1">{value}</div>
      {sub && <div className="text-xs text-muted-foreground mt-0.5">{sub}</div>}
    </div>
  );
}

export function Prose({ h, p, testid }) {
  return (
    <section data-testid={testid}>
      <h2 className="text-base md:text-lg font-display font-bold text-ochre uppercase tracking-wider mb-2">{h}</h2>
      <p className="text-base leading-relaxed text-ink/85">{p}</p>
    </section>
  );
}

export function CompareTable({ data, c, lang }) {
  const blocs = { arab: data.arab, ...data.blocs };
  const rows = [
    ["members", (b) => fmtNumber(b.members, lang)],
    ["population", (b) => fmtBig(b.population, lang, "M")],
    ["area", (b) => fmtBig(b.area_km2, lang, "Mkm2")],
    ["gdp", (b) => fmtBig(b.gdp_usd, lang, "T")],
    ["gdp_pc", (b) => `${fmtNumber(Math.round(b.gdp_usd / b.population / 100) * 100, lang)} $`],
    ["median_age", (b) => `${fmtNumber(b.median_age, lang)} ${c.table.years}`],
  ];
  return (
    <div className="overflow-x-auto rounded-2xl border border-stoneLine bg-card" data-testid="compare-table">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-[10px] uppercase tracking-wider text-muted-foreground border-b border-stoneLine">
            <th className="text-start px-3 sm:px-4 py-3 font-medium">{c.table.metric}</th>
            {BLOC_KEYS.map((k) => <th key={k} className={`text-end px-3 sm:px-4 py-3 font-medium ${k === "arab" ? "text-ochre" : ""}`}>{c.table[k]}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map(([key, fmt]) => (
            <tr key={key} className="border-b border-stoneLine/60 last:border-0" data-testid={`compare-row-${key}`}>
              <td className="px-3 sm:px-4 py-2.5 text-ink/80">{c.table[key]}</td>
              {BLOC_KEYS.map((k) => <td key={k} className={`px-3 sm:px-4 py-2.5 text-end stat-number whitespace-nowrap ${k === "arab" ? "text-ink font-semibold bg-ochre/5" : "text-ink/80"}`}>{fmt(blocs[k])}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function Points({ h, items, good }) {
  const Icon = good ? CheckCircle2 : AlertTriangle;
  return (
    <div className={`rounded-2xl border p-4 sm:p-5 ${good ? "border-oasis/30 bg-oasis/5" : "border-ochre/30 bg-ochre/5"}`} data-testid={good ? "strengths" : "weaknesses"}>
      <h3 className={`font-display font-bold text-base md:text-lg mb-3 ${good ? "text-oasis" : "text-ochre"}`}>{h}</h3>
      <ul className="space-y-2">
        {items.map((it) => (
          <li key={it} className="flex gap-2 text-sm leading-snug text-ink/85"><Icon className={`w-4 h-4 mt-0.5 shrink-0 ${good ? "text-oasis" : "text-ochre"}`} /> {it}</li>
        ))}
      </ul>
    </div>
  );
}

export default function Overview() {
  const { lang, t } = useLang();
  const c = OVERVIEW[lang] || OVERVIEW.en;
  const { data } = useQuery({ queryKey: ["overview"], queryFn: fetchOverview });
  const a = data?.arab;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-8 py-8 sm:py-12 space-y-10" data-testid="overview-page">
      <header>
        <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-ochre font-semibold mb-2"><Globe2 className="w-4 h-4" /> {c.kicker}</div>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display font-bold text-ink tracking-tight leading-none">{c.title}</h1>
        <p className="mt-4 text-base leading-relaxed text-ink/80 max-w-3xl">{c.intro}</p>
      </header>

      <IntroSection />

      {a && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          <StatCard icon={Landmark} label={c.table.members} value={fmtNumber(a.members, lang)} sub={`+${a.territories}`} testid="stat-members" />
          <StatCard icon={Users} label={c.table.population} value={fmtBig(a.population, lang, "M")} sub={`${c.table.median_age}: ${fmtNumber(a.median_age, lang)} ${c.table.years}`} testid="stat-population" />
          <StatCard icon={MapIcon} label={c.table.area} value={fmtBig(a.area_km2, lang, "Mkm2")} testid="stat-area" />
          <StatCard icon={Globe2} label={c.table.gdp} value={fmtBig(a.gdp_usd, lang, "T")} sub={a.gdp_year ? `${a.gdp_year} · ${a.gdp_countries}/23` : undefined} testid="stat-gdp" />
        </div>
      )}

      <div className="rounded-2xl bg-ink text-sand p-5 sm:p-6" data-testid="if-one-country">
        <div className="text-[10px] uppercase tracking-[0.2em] text-ochre mb-2">{c.if_one}</div>
        <ul className="grid sm:grid-cols-3 gap-3">
          {c.ranks.map((r, i) => (
            <li key={r} className="flex gap-3 items-start"><span className="stat-number text-3xl text-ochre leading-none">{i + 1}</span><span className="text-sm leading-snug text-sand/90">{r}</span></li>
          ))}
        </ul>
      </div>

      <div className="grid md:grid-cols-2 gap-8">
        <Prose {...c.sections.history} testid="section-history" />
        <Prose {...c.sections.people} testid="section-people" />
      </div>
      <Prose {...c.sections.economy} testid="section-economy" />

      {a?.top_gdp?.length > 0 && (
        <div data-testid="top-gdp">
          <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground mb-2">{c.table.top_gdp}</div>
          <div className="flex flex-wrap gap-2">
            {a.top_gdp.map((t) => (
              <Link key={t.code} to={`/?c=${t.code}`} className="inline-flex items-center gap-2 rounded-full border border-stoneLine bg-card px-3 py-1.5 text-sm hover:border-ochre hover:text-ochre transition-colors" data-testid={`top-gdp-${t.code}`}>
                <img src={t.flag_url} alt="" className="w-5 h-3.5 object-cover rounded-sm border border-stoneLine" />
                {t.short_names[lang] || t.short_names.en}
                <span className="stat-number text-xs text-muted-foreground">{fmtNumber(Math.round(t.gdp_usd / 1e9), lang)} G$</span>
              </Link>
            ))}
          </div>
        </div>
      )}

      <section className="space-y-4" data-testid="section-compare">
        <Prose {...c.sections.compare} />
        {data && <CompareTable data={data} c={c} lang={lang} />}
        {a && <p className="text-[11px] text-muted-foreground">{c.table.source.replace("{n}", a.gdp_countries)}</p>}
      </section>

      <div className="grid md:grid-cols-2 gap-4">
        <Points {...c.sections.strengths} good />
        <Points {...c.sections.weaknesses} />
      </div>

      <section className="rounded-2xl border border-stoneLine bg-card/70 p-5 sm:p-6" data-testid="section-conclusion">
        <Prose {...c.sections.conclusion} />
        <Link to="/" className="mt-4 inline-flex items-center gap-2 text-sm text-ochre hover:underline" data-testid="overview-back-map">
          <MapIcon className="w-4 h-4" /> {t("map")} <ArrowRight className="w-4 h-4 rtl:rotate-180" />
        </Link>
      </section>
    </div>
  );
}
